import type {
  AggregatedBundlePriceDto,
  AggregatedPaymentDto,
} from '~/models/ListingProduct.api.model';
import type {
  PriceModalInput,
  PriceModalPlanInput,
  PriceModalResult,
} from '~/models/pricing/PriceDetailsModalTypes.model';
import { buildPriceModal } from '~/utils/pricing/priceDetailsModal';
import { buildPdpPeriodTotals } from '~/utils/pricing/pdpPriceDetailsPeriods';
import type { FamilyPlanDetails } from '~/models/cellphone_page/FamilyPlanDetails.model';

function pickPdpSavingsPerMonth(
  payment: AggregatedPaymentDto | undefined,
  familyDetails: FamilyPlanDetails | undefined,
  isFamilySet: boolean,
): number | undefined {
  if (!payment) {
    return undefined;
  }

  if (isFamilySet) {
    const familyMonthlyFee = familyDetails?.familyPlansTotalMonthlyFee || 0;
    const familyRegularFee =
      familyDetails?.familyPlansTotalRegularMonthlyFee ||
      familyDetails?.familyPlansTotalMonthlyFee ||
      0;

    // (Från maj 2025)
    // Don't show savings per month if there are family plans with discounts
    // because the number will be obviously wrong.
    // This is because family plan savings per month will be over the course of the family discount,
    // and the rest is over the course of the part payment period, and they need to be
    // displayed separately
    if (familyMonthlyFee !== familyRegularFee) {
      return undefined;
    }
  }

  const savings = payment.SavingsMonthly;
  return savings && savings > 0 ? savings : undefined;
}

export function usePdpPriceDetailsModal() {
  const store = useMyProductPageStore();
  const planStore = usePlanStore();

  const planRows = computed<PriceModalPlanInput[]>(() => {
    const bundlePrice = store.currentBundlePrice as
      | AggregatedBundlePriceDto
      | undefined;
    const familyDetails = store.productState.familyPlanDetails;
    const plans: PriceModalPlanInput[] = [];
    const plan = bundlePrice?.Plan;

    // Vanligt huvudabonnemang om inte familjeabonnemang
    if (
      plan &&
      (!store.isFamilyPlanSet ||
        familyDetails?.isNewCustomer === undefined ||
        familyDetails?.isNewCustomer === true)
    ) {
      plans.push({
        planName: planStore.selectPlanProductName(plan.PlanId, plan.PlanName),
        monthlyFee: plan.MonthlyFee,
        regularMonthlyFee: plan.RegularMonthlyFee,
        offerValidityInMonthsDiscount: plan.OfferValidityInMonthsDiscount ?? 0,
        quantity: 1,
        dataAllowance: plan.DataAllowance,
        regularDataAllowance: plan.RegularDataAllowance,
        isFamily: plan.IsFamilyPlan,
      });
    }

    // Familjeabonnemang
    if (store.isFamilyPlanSet && familyDetails?.familyPlanSummaryItems) {
      for (const item of familyDetails.familyPlanSummaryItems) {
        if (item.Plan?.IsFamilyPlan) {
          plans.push({
            planName: planStore.selectPlanProductName(
              item.Plan.PlanId,
              item.Plan.PlanName,
            ),
            monthlyFee: item.Plan.MonthlyFee,
            regularMonthlyFee: item.Plan.RegularMonthlyFee,
            offerValidityInMonthsDiscount:
              item.Plan.OfferValidityInMonthsDiscount ?? 0,
            quantity: 1,
            dataAllowance: item.Plan.DataAllowance,
            regularDataAllowance: item.Plan.RegularDataAllowance,
            isFamily: item.Plan.IsFamilyPlan,
          });
        }
      }
    }

    return plans;
  });

  const priceDetails = computed<PriceModalResult | null>(() => {
    const bundlePrice = store.currentBundlePrice as
      | AggregatedBundlePriceDto
      | undefined;

    if (!bundlePrice) {
      return null;
    }

    const periodResult = buildPdpPeriodTotals({
      payment: store.currentPaymentData,
      bundlePrice,
      familyDetails: store.productState.familyPlanDetails,
      isFamilySet: store.isFamilyPlanSet,
      discountedTotal: store.currentTotalMonthlyPrice,
      regularTotal: store.currentTotalRegularMonthlyPrice,
    });

    const input: PriceModalInput = {
      plans: planRows.value,
      device:
        store.currentPaymentData && store.product?.ProductName
          ? {
              productName: store.product.ProductName,
              downPayment: store.currentPaymentData.DownPayment,
              regularDownPayment: store.currentPaymentData.RegularDownPayment,
              paymentTimeMonths: store.currentPaymentData.Time,
              isDirectPayment: store.currentPaymentData.Time === 1,
            }
          : undefined,
      bindingPeriodMonths: store.selectedBindingPeriod,
      totalCostFromBackend: store.totalCostOverBindingPeriod,
      totalSavingsFromBackend: store.currentTotalSavings || undefined,
      savingsPerMonthFromBackend: pickPdpSavingsPerMonth(
        store.currentPaymentData,
        store.productState.familyPlanDetails,
        store.isFamilyPlanSet,
      ),
      periodTotalsFromBackend: periodResult?.periods,
      thereafterMonthlyTotal: periodResult?.thereafterTotal,
    };

    return buildPriceModal(input);
  });

  return { priceDetails };
}
